<script>
import Config from '@/config';
import {mapGetters} from 'vuex';
import Main from '@/mixins/main';
import Size from '@/mixins/size';
import {drivingRoute} from '@/libs/map';
import Process from '@/components/process';
import Battery from '@/components/battery';
import {getStation, getStationGuns} from '@/api';
import {Tag, Image, NoticeBar, PullRefresh, ActionSheet, GoodsAction, GoodsActionIcon, GoodsActionButton, Field} from 'vant';

export default {
  name: 'Station',
  mixins: [
    Main,
    Size
  ],
  components: {
    Process,
    Battery,
    [Tag.name]: Tag,
    [Image.name]: Image,
    [Field.name]: Field,
    [NoticeBar.name]: NoticeBar,
    [PullRefresh.name]: PullRefresh,
    [ActionSheet.name]: ActionSheet,
    [GoodsAction.name]: GoodsAction,
    [GoodsActionIcon.name]: GoodsActionIcon,
    [GoodsActionButton.name]: GoodsActionButton
  },
  props: {
    stationCode: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      guns: [],
      station: {},
      distance: '',
      duration: '',
      timeBuckets: [],
      currentPriceRate: '',
      currentTimeRange: '',
      showRateActionSheet: false,

      refreshing: false
    };
  },
  watch: {
    $route(to) {
      if (to.name === 'station') {
        this.getStation(to.params.stationCode);
      }
    }
  },
  mounted() {
    window.document.title = '速电通-站点详情';

    this.showRateActionSheet = false;

    this.getChargingOrder();
    this.getStation(this.stationCode);
  },
  computed: {
    ...mapGetters([
      'centerPoint'
    ]),
    terminalListHeight() {
      return this.height - 290 - (this.chargingOrder ? 46 : (this.station && this.station.notice ? 32 : 0))
    },
    stationFirstImage() {
      return this.station && this.station.pictures ? this.station.pictures[0] + '?x-oss-process=image/resize,m_pad,h_360,w_480,color_ededed' : '';
    }
  },
  methods: {
    refresh() {
      if (this.station) {
        this.getStation(this.station.code);
      }
    },
    getStation(stationCode) {
      getStation(stationCode, true).then(response => {
        if (response.data.code === Config.successCode) {
          response.data.body.image = response.data.body.pictures[0];
          this.station = response.data.body;

          this.timeBuckets = [];
          response.data.body.timeBuckets.forEach((value) => {
            value.priceRate = (value.serviceRate + value.electricRate).toFixed(2);
            if (value.isCurrent) {
              this.currentPriceRate = value.priceRate;
              this.currentTimeRange = value.startTime + '-' + value.endTime;
            }
            this.timeBuckets.push(value);
          });

          drivingRoute(this.centerPoint, response.data.body, (distance, duration) => {
            this.distance = distance;
            this.duration = duration;
          });

          getStationGuns(stationCode).then(response => {
            if (response.data.code === Config.successCode) {
              this.guns = response.data.body.map(value => {
                if (!value.gunState.onLine) {
                  value.soc = 100;
                  value.text = '离线';
                  value.color = '#808080';
                } else if (value.gunState.workStatus === 'CHARGING') {
                  value.color = '#F23F34';
                  value.soc = value.gunState.soc;
                  value.text = (value.gunState.soc) + '%';
                } else if (value.gunState.workStatus === 'IDLE') {
                  value.soc = 100;
                  value.text = '空闲中';
                  value.color = '#009251';
                } else {
                  value.soc = 100;
                  value.text = '占用中';
                  value.color = '#FFC344';
                }
                return value;
              });
            }
          }).finally(() => {
            this.refreshing = false;
          });
        }
      }).finally(() => {
        this.refreshing = false;
      });
    },
    toHome() {
      this.$router.replace({path: '/'});
    },
    toCharge(gunCode, sn) {
      this.$router.push({name: 'charge', params: {gunCode, sn}});
    }
  }
}
</script>

<template>
  <van-pull-refresh v-model="refreshing" @refresh="refresh" id="station">
    <van-notice-bar v-if="!chargingOrder && station.notice" color="#999999" backgroundColor="#eeeeee" :scrollable="false" :text="station.notice">
      <div slot="left-icon" class="sdt-notice"/>
    </van-notice-bar>
    <div v-if="chargingOrder" class="charging" @click="toCharge(chargingOrder.gunCode, chargingOrder.sn)">
      <div class="left">
        <battery/>
        <div class="status">充电中
          <template v-if="chargingOrder.opcode">(操作码:{{ chargingOrder.opcode }})</template>
        </div>
      </div>
      <div class="sdt-right"/>
    </div>
    <div class="top">
      <van-image width="112" height="90" radius="4" :src="stationFirstImage"/>
      <div class="info">
        <div class="name">{{ station.name }}</div>
        <div class="middle">
          <div class="left">
            <div class="guns">
              <div class="idle-num">{{ station.idleGunNum }}</div>
              <div class="word">空闲/{{ station.gunNum }}</div>
              <div class="type">快</div>
            </div>
            <div class="address">
              <div class="sdt-location"></div>
              <div class="content">{{ station.address }}</div>
            </div>
          </div>
          <div class="location" @click.stop="navigation(station.longitude, station.latitude)">
            <div class="sdt-navigation"></div>
            <div class="distance">{{ distance }}</div>
          </div>
        </div>
        <div class="tags">
          <van-tag plain v-for="(tag, k) in station.tags" :key="k">{{ tag }}</van-tag>
        </div>
      </div>
    </div>
    <div class="price">
      <div class="title">当前计费时段</div>
      <div class="time-range">{{ currentTimeRange }}</div>
      <div class="to-detail" @click="showRateActionSheet = true">
        <div>价格详情</div>
        <div class="sdt-right"/>
      </div>
    </div>
    <div class="terminal">
      <div class="title">当前终端详情</div>
      <!-- <div :style="{height: terminalListHeight + 'px'}" class="list">-->
      <div class="list">
        <div class="item" v-for="(gun, key) in guns" :key="key">
          <process :size="48" :stroke-width="4" layer-color="#EEEEEE" :value="gun.soc" :color="gun.color" :text="gun.text"/>
          <div class="info">
            <div class="label">
              <div class="park">{{ gun.showCode ? gun.showCode : gun.code }}</div>
              <div v-if="gun.soc !== 100" :style="{backgroundColor: gun.color}" class="remains">
                预计{{ gun.gunState.remainsChargeMinutes }}分钟后充满
              </div>
            </div>
            <div class="content">
              <div class="standard">{{ gun.interfaceStandardShow }}</div>
              <div class="split-line"></div>
              <div class="type-power">{{ gun.gunLineTypeShow }} {{ gun.power }}kw</div>
            </div>
          </div>
          <div class="trouble"><img v-if="gun.hasTrouble" src="../assets/images/trouble.png"/></div>
        </div>
      </div>
    </div>

    <van-action-sheet closeable close-on-click-overlay v-model="showActionSheet" @cancel="inputMoneyConfirm" @close="showActionSheet = false" cancel-text="确定" description="预付金额充电">
      <van-field center clearable v-model="payToChargeMoney" type="number" label="预付金额" title-width="80px" placeholder="请输入预付充电金额" :border="false"></van-field>
      <div class="action-sheet-item-description">充电完成后，剩余金额自动原路退回，失败全额退回</div>
    </van-action-sheet>

    <van-action-sheet v-model="showRateActionSheet" @cancel="showRateActionSheet = false" @close="showRateActionSheet = false" close-on-click-overlay close-on-popstate safe-area-inset-bottom cancel-text="取消" title="时段价格详情">
      <div class="time-range-list">
        <div class="item" v-for="(timeRange, key) in timeBuckets" :key="key">
          <div class="left">
            <div class="range">
              <div class="time" :class="{current: timeRange.isCurrent}">{{ timeRange.startTime }}-{{ timeRange.endTime }}</div>
              <div class="current-tag" v-if="timeRange.isCurrent">当前</div>
            </div>
            <div class="rate">电费{{ timeRange.electricRate }}元/度+服务费{{ timeRange.serviceRate }}元/度</div>
          </div>
          <div class="price" :class="{current: timeRange.isCurrent}">
            <div class="currency">￥</div>
            <div class="value">{{ timeRange.priceRate }}</div>
            <div class="unit">/度</div>
          </div>
        </div>
      </div>
    </van-action-sheet>

    <van-goods-action safe-area-inset-bottom>
      <van-goods-action-icon text="首页" @click="toHome">
        <i slot="icon" class="sdt-home"></i>
      </van-goods-action-icon>
      <a :href="'tel:' + station.stationTel || station.managerInfo">
        <van-goods-action-icon text="客服">
          <i slot="icon" class="sdt-customer-service"></i>
        </van-goods-action-icon>
      </a>
      <div class="split-line"></div>
      <div class="current-price">
        <div class="currency">￥</div>
        <div class="value">{{ currentPriceRate }}</div>
        <div class="unit">/度</div>
      </div>
      <van-goods-action-button color="#009251" text="扫码充电" @click="scan"/>
    </van-goods-action>
  </van-pull-refresh>
</template>

<style scoped>
#station {
  background-color: #F6F9F8;
}

#station .van-notice-bar {
  height: 32px;
  font-size: 13px;
  line-height: 32px;
}

#station .van-notice-bar .sdt-notice {
  font-size: 11px;
  margin-right: 4px;
}

#station .charging {
  color: white;
  padding: 12px;
  font-size: 14px;
  font-weight: 500;
  background-color: rgba(0, 0, 0, 0.5);

  display: flex;
  align-items: center;
  justify-content: space-between;
}

#station .charging .left {
  display: flex;
  align-items: center;
}

#station .charging .left .status {
  margin-left: 4px;
  padding-bottom: 2px;
}

#station .top {
  display: flex;
  padding: 12px;
  align-items: center;
  background-color: white;
  justify-content: space-between;
}

#station .top .info {
  display: flex;
  flex-direction: column;
  width: calc(100% - 124px);
}

#station .top .info .name {
  color: #333333;
  font-size: 16px;
  font-weight: bold;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

#station .top .info .middle {
  margin-top: 14px;

  display: flex;
  align-items: center;
  justify-content: space-between;
}

#station .top .info .middle .left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

#station .top .info .middle .left .guns {
  display: flex;
  color: #999999;
  font-size: 14px;
  align-items: center;
}

#station .top .info .middle .left .guns .idle-num {
  color: #009251;
  font-weight: bold;
}

#station .top .info .middle .left .guns .word {
  margin-left: 1px;
}

#station .top .info .middle .left .guns .type {
  width: 18px;
  height: 18px;
  opacity: 0.6;
  font-size: 13px;
  margin-left: 4px;
  text-align: center;
  color: rgba(253, 93, 15, 1);
  border-radius: 8px 8px 8px 0;
  background-color: rgba(253, 93, 15, 0.1);;
}

#station .top .info .middle .left .address {
  display: flex;
  color: #999999;
  margin-top: 8px;
  align-items: center;
}

#station .top .info .middle .left .address .sdt-location {
  color: #C2C2C2;
  font-size: 10px;
}

#station .top .info .middle .left .address .content {
  font-size: 12px;
  margin-left: 2px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

#station .top .info .middle .location {
  display: flex;
  color: #333333;
  align-items: center;
  flex-direction: column;
}

#station .top .info .middle .location .sdt-navigation {
  font-size: 18px;
}

#station .top .info .middle .location .distance {
  margin-top: 8px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0;
}

#station .top .info .tags {
  color: #C2C2C2;
  font-size: 11px;
  margin-top: 4px;
}

#station .top .info .tags .van-tag + .van-tag {
  margin-left: 4px;
}

#station .price {
  display: flex;
  color: #333333;
  margin-top: 4px;
  font-size: 14px;
  font-weight: bold;
  padding: 12px 16px;
  align-items: center;
  background-color: white;
  justify-content: space-between;
}

#station .price .time-range {
  color: #009251;
  font-size: 15px;
}

#station .price .to-detail {
  display: flex;
  color: #9d9d9d;
  font-size: 13px;
  font-weight: 400;
  align-items: center;
}

#station .price .to-detail .sdt-right {
  color: #C2C2C2;
  margin-left: 10px;
  font-weight: bold;
}

#station .terminal {
  margin-top: 4px;
  background-color: white;
}

#station .terminal .title {
  color: #333333;
  font-size: 14px;
  font-weight: bold;
  padding: 12px 16px;
}

#station .terminal .list {
  overflow-y: auto;
}

#station .terminal .item {
  display: flex;
  padding: 8px 0 8px 12px;
  align-items: center;
  border-bottom: 1px solid #F8F8F8;
}

#station .terminal .item .info {
  flex: 1;
  margin-left: 18px;
}

#station .terminal .item .info .label {
  display: flex;
  line-height: 22px;
  align-items: center;
}

#station .terminal .item .info .label .park {
  color: #333333;
  font-size: 16px;
  font-weight: bold;
}

#station .terminal .item .info .label .remains {
  opacity: 0.4;
  color: white;
  font-size: 10px;
  padding: 2px 4px;
  border-radius: 4px;
  margin-left: 16px;
}

#station .terminal .item .info .content {
  display: flex;
  color: #999999;
  font-size: 14px;
  margin-top: 4px;
  align-items: center;
}

#station .terminal .item .info .content .split-line {
  width: 1px;
  height: 16px;
  margin: 0 24px;
  background-color: #eeeeee;
}

#station .terminal .item .trouble,
#station .terminal .item .trouble img {
  width: 38px;
  height: 64px;
}

#station .time-range-list .item {
  display: flex;
  padding: 6px 0;
  margin: 0 12px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #EEEEEE;
}

#station .time-range-list .item .left .range {
  display: flex;
  align-items: center;
}

#station .time-range-list .item .left .range .time {
  color: #333333;
  font-size: 16px;
  font-weight: bold;
}

#station .time-range-list .item .left .range .time.current {
  color: #009251;
}

#station .time-range-list .item .left .range .current-tag {
  width: 32px;
  height: 14px;
  color: white;
  margin-left: 4px;
  font-size: 10px;
  font-weight: 500;
  text-align: center;
  background-color: #FD6E2F;
  border-radius: 7px 7px 7px 0;
}

#station .time-range-list .item .left .rate {
  margin-top: 4px;
  color: #999999;
  font-size: 12px;
  font-weight: 500;
}

#station .time-range-list .item .price {
  display: flex;
  color: #009251;
  font-size: 18px;
  align-items: center;
}

#station .time-range-list .item .price .unit {
  color: #333333;
}

#station .time-range-list .item:last-child {
  margin: 0;
  padding: 4px 12px;
}

#station .action-sheet-item-description {
  color: #C2C2C2;
  font-size: 12px;
  padding: 0 16px 10px 16px;
}

#station .van-goods-action {
  height: 50px;
  font-weight: bold;
  padding-left: 15px;
  border-radius: 25px;
  margin: 0 8px 8px 8px;
  background: rgba(255, 255, 255, 1);
  box-shadow: 0 2px 5px 0 rgba(52, 52, 52, 0.26);
}

#station .van-goods-action .van-goods-action-icon {
  height: auto;
}

#station .van-goods-action .split-line {
  width: 1px;
  height: 28px;
  margin-left: 16px;
  background-color: #999999;;
}

#station .van-goods-action .current-price {
  display: flex;
  color: #000000;
  padding: 0 16px;
  font-size: 14px;
  font-weight: bold;
  position: relative;
  align-items: center;
}

#station .van-goods-action .current-price .currency {
  font-size: 12px;
  position: relative;
}

#station .van-goods-action .current-price .value {
  font-size: 24px;
  font-weight: 600;
}

#station .van-goods-action .current-price .unit {
  font-size: 12px;
  position: relative;
}

#station .van-goods-action .van-button {
  height: 50px;
  margin-right: 0;
  border-top-left-radius: 20px;
}
</style>
